/*#######################################
              Importaciones
#########################################*/

import React, { useState, useRef, useEffect } from 'react';
import Swal from 'sweetalert2';
import './Contact.css';
import { FaUser, FaEnvelope, FaPhone, FaCommentDots, FaPaperPlane } from 'react-icons/fa';
import { API_URLS } from '../../config/apiConfig';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/*#######################################
                Logica
#########################################*/
const Contact = () => {
    const titleRef = useRef(null);
    const [formData, setFormData] = useState({
        nombre: '',
        email: '',
        telefono: '',
        mensaje: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (titleRef.current) {
            titleRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validación simple de email antes de enviar
        if (!EMAIL_REGEX.test(formData.email.trim())) {
            Swal.fire({
                icon: 'warning',
                title: 'Email inválido',
                text: 'Por favor revisá el formato de tu email.',
                confirmButtonColor: '#00f5ff',
                background: '#1a1a2e',
                color: '#e2e8f0',
            });
            return;
        }

        setIsSubmitting(true);

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000);

        try {
            const response = await fetch(API_URLS.sendContactEmail, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData),
                signal: controller.signal,
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Error al enviar mensaje');
            }

            Swal.fire({
                icon: 'success',
                title: '¡Mensaje enviado!',
                text: 'Nos pondremos en contacto contigo pronto. También te enviamos una confirmación a tu email.',
                confirmButtonColor: '#00f5ff',
                background: '#1a1a2e',
                color: '#e2e8f0',
            });

            setFormData({ nombre: '', email: '', telefono: '', mensaje: '' });
        } catch (error) {
            console.error('Error al enviar mensaje:', error);
            const isAbort = error.name === 'AbortError';
            Swal.fire({
                icon: 'error',
                title: isAbort ? 'Tiempo de espera agotado' : 'Error',
                text: isAbort
                    ? 'No pudimos contactar el servidor. Revisá tu conexión e intentá de nuevo.'
                    : (error.message || 'Hubo un problema al enviar tu mensaje. Por favor, intenta nuevamente.'),
                background: '#1a1a2e',
                color: '#e2e8f0',
            });
        } finally {
            clearTimeout(timeoutId);
            setIsSubmitting(false);
        }
    };

    return (
        <section className="contact-section">
            <div className="contact-content">
                <h1 className="contact-title" ref={titleRef}>
                    <span className="title-icon">📬</span>
                    Contáctanos
                </h1>
                <p className="contact-subtitle">
                    ¿Tienes alguna duda o consulta? Escríbenos y te responderemos lo antes posible.
                </p>

                <form className="contact-form" onSubmit={handleSubmit}>
                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="nombre">
                                <FaUser className="input-icon" />
                                Nombre
                            </label>
                            <input
                                type="text"
                                id="nombre"
                                name="nombre"
                                placeholder="Tu nombre completo"
                                value={formData.nombre}
                                onChange={handleChange}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="email">
                                <FaEnvelope className="input-icon" />
                                Email
                            </label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                placeholder="tu@email.com"
                                value={formData.email}
                                onChange={handleChange}
                                required
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="telefono">
                            <FaPhone className="input-icon" />
                            Teléfono <span className="optional">(opcional)</span>
                        </label>
                        <input
                            type="tel"
                            id="telefono"
                            name="telefono"
                            placeholder="+54 9 261 123 4567"
                            value={formData.telefono}
                            onChange={handleChange}
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="mensaje">
                            <FaCommentDots className="input-icon" />
                            Mensaje
                        </label>
                        <textarea
                            id="mensaje"
                            name="mensaje"
                            rows="5"
                            placeholder="Escribe tu consulta aquí..."
                            value={formData.mensaje}
                            onChange={handleChange}
                            required
                        />
                    </div>

                    <button type="submit" className="btn-submit" disabled={isSubmitting}>
                        {isSubmitting ? (
                            <span className="loading-text">Enviando...</span>
                        ) : (
                            <>
                                <FaPaperPlane className="btn-icon" />
                                Enviar mensaje
                            </>
                        )}
                    </button>
                </form>
            </div>
        </section>
    );
};

export default Contact;